import React from 'react';
import {
  Button,
  Card,
  TextInput,
  Modal,
  ToastProvider,
  useToast,
  DataTable,
  DataTableColumn,
  Select,
  SegmentedControl,
  TabBar,
  Sidebar,
  MenuGrid,
  MenuGridItem
} from '../design-system';

const MENU_CATEGORIES = [
  { id: 'apps', label: 'Starters' },
  { id: 'mains', label: 'Mains' },
  { id: 'sides', label: 'Sides' },
  { id: 'desserts', label: 'Desserts' }
];

const INITIAL_MENU_ITEMS: MenuGridItem[] = [
  { id: 'm1', categoryId: 'apps', name: 'Caesar Salad', description: 'Romaine, croutons, parmesan, anchovy dressing', price: 14 },
  { id: 'm2', categoryId: 'apps', name: 'Calamari', description: 'Lightly battered, lemon aioli', price: 17 },
  { id: 'm3', categoryId: 'apps', name: 'Shishito Peppers', description: 'Blistered, sea salt', price: 11, available: false },
  { id: 'm4', categoryId: 'mains', name: 'Classic Burger', description: '8oz patty, cheddar, lettuce, tomato, brioche', price: 21 },
  { id: 'm5', categoryId: 'mains', name: 'Halibut', description: 'Pan-seared, lemon caper butter', price: 34 },
  { id: 'm6', categoryId: 'mains', name: 'Mushroom Risotto', description: 'Wild mushrooms, parmesan, truffle oil', price: 26 },
  { id: 'm7', categoryId: 'mains', name: 'NY Strip', description: '12oz, herb butter, choice of side', price: 42 },
  { id: 'm8', categoryId: 'sides', name: 'Truffle Fries', price: 9 },
  { id: 'm9', categoryId: 'sides', name: 'Brussels Sprouts', description: 'Honey balsamic glaze', price: 10 },
  { id: 'm10', categoryId: 'sides', name: 'Mac & Cheese', description: 'Three-cheese blend, crispy top', price: 12 },
  { id: 'm11', categoryId: 'desserts', name: 'Crème Brûlée', price: 11 },
  { id: 'm12', categoryId: 'desserts', name: 'Chocolate Tart', description: 'Raspberry coulis', price: 12 }
];

type DemoRow = { id: string; name: string; status: 'active' | 'draft' | 'archived'; total: number };

const DEMO_ROWS: DemoRow[] = Array.from({ length: 23 }, (_, i) => ({
  id: `row_${i + 1}`,
  name: `Menu item ${i + 1}`,
  status: (['active', 'draft', 'archived'] as const)[i % 3],
  total: Math.round(100 + Math.random() * 900)
}));

const COLUMNS: DataTableColumn<DemoRow>[] = [
  { key: 'name', header: 'Name', accessor: (r) => r.name, sortable: true },
  { key: 'status', header: 'Status', accessor: (r) => r.status, sortable: true },
  {
    key: 'total',
    header: 'Total',
    accessor: (r) => `$${r.total.toFixed(2)}`,
    sortable: true,
    compare: (a, b) => a.total - b.total,
    align: 'right'
  }
];

const ToastButtons: React.FC = () => {
  const { show } = useToast();
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <Button onClick={() => show({ tone: 'success', title: 'Saved', description: 'Changes persisted.' })}>
        Success toast
      </Button>
      <Button variant="secondary" onClick={() => show({ tone: 'info', title: 'FYI', description: 'An informational message.' })}>
        Info toast
      </Button>
      <Button variant="destructive" onClick={() => show({ tone: 'error', title: 'Error', description: 'Something went wrong.' })}>
        Error toast
      </Button>
    </div>
  );
};

const Inner: React.FC = () => {
  const [tab, setTab] = React.useState<'buttons' | 'data' | 'forms' | 'layout' | 'restaurant'>('buttons');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [segment, setSegment] = React.useState<'day' | 'week' | 'month'>('day');
  const [selected, setSelected] = React.useState('usd');
  const [search, setSearch] = React.useState('');

  const sidebar = (
    <Sidebar
      aria-label="Demo sections"
      sections={[
        {
          title: 'Gallery',
          items: [
            { key: 'buttons', label: 'Buttons', active: tab === 'buttons', onSelect: () => setTab('buttons') },
            { key: 'data', label: 'Data display', active: tab === 'data', onSelect: () => setTab('data') },
            { key: 'forms', label: 'Forms', active: tab === 'forms', onSelect: () => setTab('forms') },
            { key: 'layout', label: 'Layout', active: tab === 'layout', onSelect: () => setTab('layout') },
            { key: 'restaurant', label: 'Restaurant', active: tab === 'restaurant', onSelect: () => setTab('restaurant') }
          ]
        }
      ]}
    />
  );

  return (
    <div data-theme="dark" style={{ display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: '100vh', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      {sidebar}
      <main style={{ padding: '32px 48px', maxWidth: 1080 }}>
        <h1 style={{ marginTop: 0 }}>BSO Design System</h1>
        <p style={{ color: 'var(--bso-text-secondary)' }}>
          Interactive gallery of primitives. Use the sidebar to switch sections.
        </p>

        <TabBar
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'buttons', label: 'Buttons' },
            { value: 'data', label: 'Data display' },
            { value: 'forms', label: 'Forms' },
            { value: 'layout', label: 'Layout' },
            { value: 'restaurant', label: 'Restaurant' }
          ]}
          aria-label="Gallery tabs"
        />

        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 32 }}>
          {tab === 'buttons' && (
            <>
              <section>
                <h2>Button variants</h2>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button>Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="destructive">Destructive</Button>
                  <Button loading>Loading</Button>
                  <Button disabled>Disabled</Button>
                </div>
              </section>
              <section>
                <h2>Sizes</h2>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Button size="sm">Small</Button>
                  <Button size="md">Medium</Button>
                  <Button size="lg">Large</Button>
                </div>
              </section>
              <section>
                <h2>Toasts</h2>
                <ToastButtons />
              </section>
              <section>
                <h2>Modal</h2>
                <Button onClick={() => setModalOpen(true)}>Open modal</Button>
                <Modal
                  open={modalOpen}
                  onClose={() => setModalOpen(false)}
                  title="Confirm action"
                  description="This is a demonstration modal. The backdrop and escape key both dismiss."
                  footer={
                    <>
                      <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
                      <Button onClick={() => setModalOpen(false)}>Confirm</Button>
                    </>
                  }
                >
                  <p>Modal body content goes here.</p>
                </Modal>
              </section>
            </>
          )}

          {tab === 'data' && (
            <section>
              <h2>DataTable</h2>
              <DataTable<DemoRow>
                columns={COLUMNS}
                rows={DEMO_ROWS}
                getRowKey={(r) => r.id}
                pageSize={8}
                zebra
              />
            </section>
          )}

          {tab === 'forms' && (
            <section style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 24 }}>
              <TextInput
                label="Search menu"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pepperoni pizza"
                hint="Case-insensitive"
              />
              <TextInput
                label="Email"
                type="email"
                required
                error="Not a valid email"
                defaultValue="not-an-email"
              />
              <Select
                label="Currency"
                options={[
                  { value: 'usd', label: 'US Dollar' },
                  { value: 'eur', label: 'Euro' },
                  { value: 'gbp', label: 'British Pound' }
                ]}
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              />
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, color: 'var(--bso-text-secondary)' }}>
                  Time range
                </label>
                <SegmentedControl<'day' | 'week' | 'month'>
                  aria-label="Time range"
                  value={segment}
                  onChange={setSegment}
                  options={[
                    { value: 'day', label: 'Day' },
                    { value: 'week', label: 'Week' },
                    { value: 'month', label: 'Month' }
                  ]}
                />
              </div>
            </section>
          )}

          {tab === 'layout' && (
            <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              <Card>
                <Card.Body>
                  <Card.Title>Plain card</Card.Title>
                  <Card.Subtitle>With body</Card.Subtitle>
                </Card.Body>
              </Card>
              <Card raised>
                <Card.Header title="Raised" subtitle="With elevation" actions={<Button size="sm" variant="ghost">Edit</Button>} />
                <Card.Body>Cards can have header, body, and footer.</Card.Body>
                <Card.Footer>
                  <Button size="sm" variant="ghost">Cancel</Button>
                  <Button size="sm">Save</Button>
                </Card.Footer>
              </Card>
              <Card interactive onClick={() => {}}>
                <Card.Body>
                  <Card.Title>Interactive</Card.Title>
                  <Card.Subtitle>Hover / focus states</Card.Subtitle>
                </Card.Body>
              </Card>
            </section>
          )}

          {tab === 'restaurant' && <MenuGridDemo />}
        </div>
      </main>
    </div>
  );
};

const MenuGridDemo: React.FC = () => {
  const [items, setItems] = React.useState(INITIAL_MENU_ITEMS);
  const [sortable, setSortable] = React.useState(true);
  const [dense, setDense] = React.useState(false);
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h2 style={{ marginTop: 0 }}>MenuGrid</h2>
        <p style={{ color: 'var(--bso-text-secondary)' }}>
          Restaurant-specific tile grid. Category tabs with live counts,
          drag-to-reorder via @dnd-kit, dense mode for POS tablets,
          automatic 86'd badge for unavailable items.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <SegmentedControl<'on' | 'off'>
          aria-label="Sortable"
          value={sortable ? 'on' : 'off'}
          onChange={(v) => setSortable(v === 'on')}
          options={[{ value: 'on', label: 'Sortable' }, { value: 'off', label: 'Static' }]}
        />
        <SegmentedControl<'normal' | 'dense'>
          aria-label="Density"
          value={dense ? 'dense' : 'normal'}
          onChange={(v) => setDense(v === 'dense')}
          options={[{ value: 'normal', label: 'Normal' }, { value: 'dense', label: 'Dense' }]}
        />
      </div>
      <MenuGrid
        items={items}
        categories={MENU_CATEGORIES}
        sortable={sortable}
        dense={dense}
        onReorder={(next) => setItems(next)}
        onItemClick={(it) => alert(`Clicked ${it.name}`)}
      />
    </section>
  );
};

export const DesignSystemDemo: React.FC = () => (
  <ToastProvider>
    <Inner />
  </ToastProvider>
);
